import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Sidebar from '../../components/Sidebar';


import { Target, Settings, DollarSign, Activity, CheckCircle } from 'lucide-react';

const ROICalculatorAnalysisEngine = () => {
  const [currency, setCurrency] = useState('ZAR');
  const [timePeriod, setTimePeriod] = useState('6 Months');
  const [advCalc, setAdvCalc] = useState({});
  const revenueChartRef = useRef(null);
  
  const [formData, setFormData] = useState({
    advBudget: '',
    advProfit: '25',
    advAOV: '',
    advNumProducts: '',
    advRevenue: '',
    advOverheads: '',
    metaPct: 20,
    focusPct: 20,
  });

  const parseNum = (val) => {
    const num = parseFloat(String(val)?.replace(/[^\d.-]/g, ''));
    return isNaN(num) ? 0 : num;
  };

  const formatInput = (val) => {
    if (!val) return '';
    return String(val);
  };

  const formatCurrency = (amount) => {
    const symbol = currency === 'ZAR' ? 'R' : '$';
    return `${symbol}${parseNum(amount)?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateAdvanced = () => {
    const budget = parseNum(formData?.advBudget);
    const profit = parseNum(formData?.advProfit) / 100;
    const aov = parseNum(formData?.advAOV);
    const numProducts = parseNum(formData?.advNumProducts);
    const revenue = parseNum(formData?.advRevenue);
    const overheads = parseNum(formData?.advOverheads);
    const metaPct = parseNum(formData?.metaPct);
    const googlePct = 100 - metaPct;

    const agencyTotal = budget * 0.15;
    const platformCosts = budget * 0.05;
    const allIn = budget + agencyTotal + platformCosts + overheads;
    const breakEven = profit > 0 ? allIn / profit : 0;
    const roas = budget > 0 ? breakEven / budget : 0;
    const comfortable = breakEven * 1.5;

    const metaBudget = (budget * metaPct) / 100;
    const googleBudget = (budget * googlePct) / 100;

    return {
      budget,
      profit,
      aov,
      numProducts,
      revenue,
      overheads,
      agencyTotal,
      platformCosts,
      allIn,
      breakEven,
      roas,
      comfortable,
      metaShare: metaPct,
      googleShare: googlePct,
      metaBudget,
      googleBudget
    };
  };

  const generateRealisticRevenue = (currentRevenue, targetRevenue) => {
    const months = [];
    const seasonalFactors = [0.85, 0.90, 1.0, 1.05, 1.10, 1.15, 1.12, 1.08, 1.05, 1.10, 1.20, 1.25];
    const startingRevenue = currentRevenue > 0 ? currentRevenue : targetRevenue;
    
    for (let i = 0; i < 12; i++) {
      const baseGrowth = 1 + (i * 0.03);
      const seasonal = seasonalFactors?.[i] || 1;
      const randomVariation = 0.95 + Math.random() * 0.1;
      const projectedRevenue = Math.round(startingRevenue * baseGrowth * seasonal * randomVariation);
      months?.push(projectedRevenue);
    }
    
    return months;
  };

  useEffect(() => {
    const calc = calculateAdvanced();
    setAdvCalc(calc);
    
    const currentRevenue = parseNum(formData?.advRevenue);
    const monthlyRevenue = generateRealisticRevenue(currentRevenue, calc?.comfortable);
    drawRevenueChart(monthlyRevenue, currentRevenue);
  }, [formData, currency]);

  const drawRevenueChart = (monthlyData, currentRevenue) => {
    const canvas = revenueChartRef?.current;
    if (!canvas) return;

    const ctx = canvas?.getContext('2d');
    const W = (canvas.width = canvas?.clientWidth * 2);
    const H = (canvas.height = canvas?.clientHeight * 2);

    ctx?.clearRect(0, 0, W, H);

    const pad = 100;
    const max = Math.max(...monthlyData, currentRevenue || 0, 1);
    const unitX = (W - pad * 2) / 11;
    const unitY = (H - pad * 2) / max;

    // Clean white background
    ctx.fillStyle = '#ffffff';
    ctx?.fillRect(0, 0, W, H);

    // Y-axis labels (currency values)
    ctx.fillStyle = '#64748b';
    ctx.font = '14px system-ui';
    ctx.textAlign = 'right';
    const symbol = currency === 'ZAR' ? 'R' : '$';
    for (let i = 0; i <= 5; i++) {
      const value = (max / 5) * i;
      const y = H - pad - (i / 5) * (H - pad * 2);
      const formattedValue = `${symbol}${value?.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
      ctx?.fillText(formattedValue, pad - 10, y + 5);
    }

    // Light grid lines
    ctx.strokeStyle = '#f1f5f9';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 5; i++) {
      const y = H - pad - (i / 5) * (H - pad * 2);
      ctx?.beginPath();
      ctx?.moveTo(pad, y);
      ctx?.lineTo(W - pad, y);
      ctx?.stroke();
    }

    // Current revenue marker
    if (currentRevenue > 0) {
      const currentY = H - pad - currentRevenue * unitY;
      ctx.strokeStyle = '#10b981';
      ctx.lineWidth = 2;
      ctx?.setLineDash([8, 4]);
      ctx?.beginPath();
      ctx?.moveTo(pad, currentY);
      ctx?.lineTo(W - pad, currentY);
      ctx?.stroke();
      ctx?.setLineDash([]);
      
      ctx.fillStyle = '#10b981';
      ctx.font = 'bold 18px system-ui';
      ctx.textAlign = 'right';
      ctx?.fillText('Current Revenue', W - pad - 10, currentY - 8);
    }

    // Gradient under line
    const gradient = ctx?.createLinearGradient(0, 0, 0, H);
    gradient?.addColorStop(0, 'rgba(59, 130, 246, 0.1)');
    gradient?.addColorStop(1, 'rgba(59, 130, 246, 0)');
    
    ctx.fillStyle = gradient;
    ctx?.beginPath();
    ctx?.moveTo(pad, H - pad);
    monthlyData?.forEach((value, i) => {
      const x = pad + i * unitX;
      const y = H - pad - value * unitY;
      ctx?.lineTo(x, y);
    });
    ctx?.lineTo(pad + (monthlyData?.length - 1) * unitX, H - pad);
    ctx?.closePath();
    ctx?.fill();

    // Main line
    ctx.strokeStyle = '#3b82f6';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx?.beginPath();
    monthlyData?.forEach((value, i) => {
      const x = pad + i * unitX;
      const y = H - pad - value * unitY;
      if (i === 0) ctx?.moveTo(x, y);
      else ctx?.lineTo(x, y);
    });
    ctx?.stroke();

    // Data points
    monthlyData?.forEach((value, i) => {
      const x = pad + i * unitX;
      const y = H - pad - value * unitY;
      
      ctx.fillStyle = '#ffffff';
      ctx?.beginPath();
      ctx?.arc(x, y, 6, 0, Math.PI * 2);
      ctx?.fill();
      
      ctx.strokeStyle = '#3b82f6';
      ctx.lineWidth = 2;
      ctx?.beginPath();
      ctx?.arc(x, y, 6, 0, Math.PI * 2);
      ctx?.stroke();
    });

    // Month labels
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    ctx.fillStyle = '#64748b';
    ctx.font = '16px system-ui';
    ctx.textAlign = 'center';
    months?.forEach((month, i) => {
      const x = pad + i * unitX;
      ctx?.fillText(month, x, H - 20);
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      <Header />
      <main className="ml-64 mt-16 p-6">
        {/* Clean Header */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                Ecommerce ROI Calculator
              </h1>
            </div>
          </div>
        </div>

        {/* Modern Metric Cards with Colored Icons */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-1">ROAS Target</p>
                <p className="text-3xl font-bold text-slate-900">
                  {advCalc?.roas?.toFixed(2)}x
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-orange-600" />
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-1">Break Even</p>
                <p className="text-3xl font-bold text-slate-900">
                  {formatCurrency(advCalc?.breakEven)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-1">Total Investment</p>
                <p className="text-3xl font-bold text-slate-900">
                  {formatCurrency(advCalc?.allIn)}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                    <Target className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
                <p className="text-sm text-slate-600 mb-1">Comfortable Target</p>
                <p className="text-3xl font-bold text-slate-900">
                  {formatCurrency(advCalc?.comfortable)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Revenue Trends Chart Section */}
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-slate-900">Revenue Score Trend</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500"></span>
                <span className="text-sm text-slate-600">Current</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-blue-500"></span>
                <span className="text-sm text-slate-600">Projected</span>
              </div>
            </div>
          </div>
          <canvas ref={revenueChartRef} className="w-full" height="300"></canvas>
        </div>

        {/* Input Grid - Cleaner Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-6">Business Configuration</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Ad Budget</label>
                <input
                  type="text"
                  value={formatInput(formData?.advBudget)}
                  onChange={(e) => handleInputChange('advBudget', e?.target?.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                  placeholder="Enter ad budget"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Average Order Value</label>
                <input
                  type="text"
                  value={formatInput(formData?.advAOV)}
                  onChange={(e) => handleInputChange('advAOV', e?.target?.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                  placeholder="Enter average order value"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Number of Products</label>
                <input
                  type="text"
                  value={formatInput(formData?.advNumProducts)}
                  onChange={(e) => handleInputChange('advNumProducts', e?.target?.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                  placeholder="Enter number of products"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  Monthly Revenue 
                  <span className="text-red-500 text-xs">*Required</span>
                </label>
                <input
                  type="text"
                  value={formatInput(formData?.advRevenue)}
                  onChange={(e) => handleInputChange('advRevenue', e?.target?.value)}
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all text-sm"
                  placeholder="Enter current monthly revenue"
                  required
                />
              </div>
              <div className="pt-4">
                <Link 
                  to="/business-settings-configuration"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  Advanced Settings
                </Link>
              </div>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-6">Cost Breakdown</h3>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">Media Spend</span>
                <strong className="text-base text-slate-900">{formatCurrency(advCalc?.budget)}</strong>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">Agency Services</span>
                <strong className="text-base text-slate-900">{formatCurrency(advCalc?.agencyTotal)}</strong>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">Platform Costs</span>
                <strong className="text-base text-slate-900">{formatCurrency(advCalc?.platformCosts)}</strong>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                <span className="text-sm text-slate-600">Overheads</span>
                <strong className="text-base text-slate-900">{formatCurrency(advCalc?.overheads)}</strong>
              </div>
              <div className="flex justify-between items-center p-4 bg-blue-50 rounded-lg border border-blue-200 mt-4">
                <span className="text-sm font-bold text-blue-900">Total Investment</span>
                <strong className="text-xl text-blue-600">{formatCurrency(advCalc?.allIn)}</strong>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-slate-200">
              <h4 className="text-base font-bold text-slate-900 mb-4">Performance Targets</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Break Even Revenue</span>
                  <strong className="text-base text-slate-900">{formatCurrency(advCalc?.breakEven)}</strong>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">ROAS Target</span>
                  <strong className="text-base text-slate-900">{advCalc?.roas?.toFixed(2)}x</strong>
                </div>
                <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                  <span className="text-sm font-bold text-green-900">Comfortable Target</span>
                  <strong className="text-lg text-green-600">{formatCurrency(advCalc?.comfortable)}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ROICalculatorAnalysisEngine;